/**
 * Waste Categories Master — Mobile-first responsive
 * Mobile: card grid (one per category) with disposition color & key params
 * Desktop: full table
 */
import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, Form, Grid, Input, InputNumber, Modal,
  Row, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd'
import {
  EditOutlined, PlusOutlined, InfoCircleOutlined,
  RetweetOutlined, ShoppingOutlined, SwapOutlined, DeleteOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { wasteApi } from '@/features/waste/api'
import type { WasteCategory } from '@/features/waste/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text, Title } = Typography
const { useBreakpoint } = Grid

const SOURCE_DEPTS = ['BLOWROOM', 'CARDING', 'DRAWING', 'COMBING', 'SIMPLEX', 'RING', 'WINDING', 'OTHER']
const WASTE_TYPES  = ['SOFT', 'HARD', 'FLY', 'NOIL', 'DUST', 'SEED']
const REENTRY_STAGES = ['BLOWROOM', 'DRAWING', 'SIMPLEX']

const DISPOSITIONS = [
  { value: 'REUSE',    label: 'Reuse Internal',  color: '#52c41a', tag: 'green',  icon: <RetweetOutlined /> },
  { value: 'SELL',     label: 'Sell External',   color: '#faad14', tag: 'gold',   icon: <ShoppingOutlined /> },
  { value: 'TRANSFER', label: 'Stock Transfer',  color: '#1677ff', tag: 'blue',   icon: <SwapOutlined /> },
  { value: 'DISPOSE',  label: 'Dispose',         color: '#ff4d4f', tag: 'red',    icon: <DeleteOutlined /> },
]

const dispMeta = (d: string) => DISPOSITIONS.find(x => x.value === d) ?? DISPOSITIONS[1]

const DEPT_COLORS: Record<string, string> = {
  BLOWROOM: 'volcano', CARDING: 'orange', DRAWING: 'gold',
  COMBING: 'lime', SIMPLEX: 'green', RING: 'cyan',
  WINDING: 'blue', OTHER: 'default',
}

export default function WasteCategoriesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<WasteCategory | null>(null)

  const canAdmin = usePermission('waste.admin')

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['waste-categories', plantId],
    queryFn: () => wasteApi.listCategories(plantId, false),
    enabled: !!plantId,
  })

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing ? wasteApi.updateCategory(editing.id, values)
              : wasteApi.createCategory({ ...values, plant_id: plantId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waste-categories'] })
      message.success(editing ? 'Updated' : 'Created')
      setOpen(false)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      waste_type: 'SOFT', default_disposition: 'SELL', max_addback_pct: 3.0, gst_rate_pct: 5.0, is_reusable: false,
    })
    setOpen(true)
  }

  const openEdit = (r: WasteCategory) => {
    setEditing(r)
    form.setFieldsValue(r)
    setOpen(true)
  }

  const isReusable = Form.useWatch('is_reusable', form)

  // ── Card view (mobile) ────────────────────────────────────────────────────
  function CategoryCard({ cat }: { cat: WasteCategory }) {
    const m = dispMeta(cat.default_disposition)
    return (
      <Card
        size="small"
        style={{
          marginBottom: 12,
          borderLeft: `4px solid ${m.color}`,
          opacity: cat.is_active ? 1 : 0.6,
        }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 14, display: 'block', wordBreak: 'break-word' }}>
              {cat.name}
            </Text>
            <Space size={4} style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 10, fontFamily: 'monospace' }}>{cat.code}</Text>
              <Tag color={DEPT_COLORS[cat.source_dept]} style={{ fontSize: 10, margin: 0 }}>{cat.source_dept}</Tag>
            </Space>
          </div>
          <Tag color={m.tag} icon={m.icon} style={{ flexShrink: 0 }}>{m.label.split(' ')[0]}</Tag>
        </div>

        <Row gutter={8} style={{ marginTop: 8 }}>
          {cat.is_reusable ? (
            <>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 10 }}>YIELD</Text>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#52c41a' }}>
                  {cat.effective_yield_pct ?? '—'}%
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 10 }}>RE-ENTRY</Text>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{cat.reentry_stage ?? '—'}</div>
              </Col>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 10 }}>MAX ADD</Text>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{cat.max_addback_pct}%</div>
              </Col>
            </>
          ) : (
            <>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 10 }}>SALE RATE</Text>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#faad14' }}>
                  {cat.standard_sale_rate ? `₹${Number(cat.standard_sale_rate).toFixed(0)}/kg` : '—'}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 10 }}>HSN / GST</Text>
                <div style={{ fontSize: 12, fontWeight: 600 }}>
                  {cat.hsn_code ?? '—'} / {cat.gst_rate_pct}%
                </div>
              </Col>
            </>
          )}
        </Row>

        {canAdmin && (
          <Button
            block icon={<EditOutlined />}
            style={{ marginTop: 10, height: 38 }}
            onClick={() => openEdit(cat)}
          >
            Edit
          </Button>
        )}
      </Card>
    )
  }

  // ── Table columns (desktop) ────────────────────────────────────────────────
  const columns = [
    { title: 'Category', key: 'cat',
      render: (_: unknown, r: WasteCategory) => (
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontSize: 13 }}>{r.name}</Text>
          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 10, fontFamily: 'monospace' }}>{r.code}</Text>
            <Tag color={DEPT_COLORS[r.source_dept]} style={{ fontSize: 10, margin: 0 }}>{r.source_dept}</Tag>
            <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>{r.waste_type}</Tag>
          </Space>
        </Space>
      ) },
    { title: 'Disposition', dataIndex: 'default_disposition', width: 150,
      render: (v: string) => {
        const m = dispMeta(v)
        return <Tag color={m.tag} icon={m.icon}>{m.label}</Tag>
      } },
    { title: 'Reusable', dataIndex: 'is_reusable', width: 90, align: 'center' as const,
      render: (v: boolean) => v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag> },
    {
      title: 'Reuse Params', key: 'reuse', width: 220,
      render: (_: unknown, r: WasteCategory) => r.is_reusable
        ? (
          <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
            <Text>Yield: <Text strong>{r.effective_yield_pct ?? '—'}%</Text> · Re-entry: <Text strong>{r.reentry_stage ?? '—'}</Text></Text>
            <Text>Max addback: <Text strong>{r.max_addback_pct}%</Text></Text>
          </Space>
        )
        : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    { title: 'Sale ₹/kg', dataIndex: 'standard_sale_rate', width: 100, align: 'right' as const,
      render: (v: any) => v ? <Text strong style={{ color: '#faad14' }}>₹{Number(v).toFixed(0)}</Text> : '—' },
    { title: 'Status', dataIndex: 'is_active', width: 90,
      render: (v: boolean) => v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag> },
    { title: '', key: 'action', width: 80,
      render: (_: unknown, r: WasteCategory) =>
        canAdmin && <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Waste Categories"
        subtitle="12 standard categories pre-seeded. Configure disposition route + reuse parameters per category."
        extra={canAdmin && (
          <Button type="primary" icon={<PlusOutlined />} size={isMobile ? 'large' : 'middle'} onClick={openCreate}>
            Add
          </Button>
        )}
      />

      <Alert
        message={
          <Space wrap size={[12, 4]}>
            <Text strong>Disposition Routes:</Text>
            {DISPOSITIONS.map(d => (
              <Tag key={d.value} color={d.tag} icon={d.icon}>{d.label}</Tag>
            ))}
          </Space>
        }
        type="info"
        style={{ marginBottom: 16 }}
      />

      {isMobile ? (
        <div>
          {isLoading ? <Card loading /> :
            categories.length === 0 ? <Card><Text>No categories yet. They auto-seed on first plant load.</Text></Card> :
            <Row gutter={[12, 0]}>
              {categories.map(c => (
                <Col xs={24} sm={12} key={c.id}>
                  <CategoryCard cat={c} />
                </Col>
              ))}
            </Row>
          }
        </div>
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table rowKey="id" columns={columns} dataSource={categories} loading={isLoading} size="middle" />
        </Card>
      )}

      <Modal
        title={editing ? `Edit: ${editing.code}` : 'Add Waste Category'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.validateFields().then(v => saveMutation.mutate(v))}
        confirmLoading={saveMutation.isPending}
        width={isMobile ? '95%' : 720}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Tabs items={[
            {
              key: 'basic', label: 'Basic',
              children: (
                <>
                  {!editing && (
                    <Form.Item name="code" label="Code"
                      rules={[{ required: true, pattern: /^[A-Z0-9_]+$/, message: 'UPPER_CASE_ONLY' }]}>
                      <Input size="large" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
                    </Form.Item>
                  )}
                  <Form.Item name="name" label="Display Name" rules={[{ required: true }]}>
                    <Input size="large" placeholder="e.g. Drawing Sliver Waste" />
                  </Form.Item>
                  <Row gutter={12}>
                    <Col xs={24} sm={8}>
                      <Form.Item name="source_dept" label="Source Dept" rules={[{ required: true }]}>
                        <Select size="large" options={SOURCE_DEPTS.map(v => ({ value: v, label: v }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item name="waste_type" label="Waste Type" rules={[{ required: true }]}>
                        <Select size="large" options={WASTE_TYPES.map(v => ({ value: v, label: v }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item name="is_reusable" label="Reusable?" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="default_disposition" label="Default Disposition" rules={[{ required: true }]}>
                    <Select size="large" options={DISPOSITIONS.map(d => ({
                      value: d.value,
                      label: <Space>{d.icon}{d.label}</Space>,
                    }))} />
                  </Form.Item>
                  <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
                  {editing && (
                    <Form.Item name="is_active" label="Active" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  )}
                </>
              ),
            },
            {
              key: 'reuse',
              label: <span>Reuse Params {!isReusable && <Tooltip title="Disabled — not reusable"><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}</span>,
              disabled: !isReusable,
              children: (
                <>
                  <Alert
                    message="True economic benefit = Avoided cotton − Reprocessing − Secondary loss − Opportunity cost"
                    type="info" showIcon style={{ marginBottom: 12 }}
                  />
                  <Row gutter={12}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="reentry_stage" label="Re-entry Stage" help="Where waste re-enters the line">
                        <Select size="large" allowClear options={REENTRY_STAGES.map(v => ({ value: v, label: v }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={12}>
                      <Form.Item name="reusability_pct" label="Reusability %">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} max={100} step={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col xs={8}>
                      <Form.Item name="max_addback_pct" label="Max Addback %">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} max={20} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={8}>
                      <Form.Item name="effective_yield_pct" label="Yield %">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} max={100} step={1} />
                      </Form.Item>
                    </Col>
                    <Col xs={8}>
                      <Form.Item name="secondary_waste_pct" label="Secondary %">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} max={100} step={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="secondary_disposal_rate" label="Secondary Disposal Rate ₹/kg">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} step={1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="reprocessing_cost_per_kg" label="Reprocessing ₹/kg (override)">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.5} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'sale', label: 'Sale / Tax',
              children: (
                <>
                  <Row gutter={12}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="standard_sale_rate" label="Sale Rate ₹/kg">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.5} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="disposal_cost_per_kg" label="Disposal Cost ₹/kg">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.5} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col xs={12}>
                      <Form.Item name="hsn_code" label="HSN Code">
                        <Input size="large" maxLength={8} placeholder="e.g. 5203" />
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item name="gst_rate_pct" label="GST %">
                        <InputNumber size="large" style={{ width: '100%' }} min={0} max={28} step={0.5} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ),
            },
          ]}/>
        </Form>
      </Modal>
    </div>
  )
}
